import { useMemo, useState } from 'react';
import { Box, FormControl, FormLabel, Stack } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import { axios } from '@/services/axios';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import DropDown from 'components/forms/new/formikFormFields/DropDown';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';
import { useCompanyForm } from 'pages/organisations/companies/v5/hooks/useCompanyForm';

const getOptionId = (item) =>
    item?.id ?? item?.value ?? item?.name ?? item?.location ?? item;

const getOptionLabel = (item) =>
    item?.name || item?.location || item?.description || item?.id || '';

const toDropdownOption = (item) => ({
    id: getOptionId(item),
    name: getOptionLabel(item),
    raw: item,
});

const WholesalerMultiSelectField = ({
    darkMode,
    endpoint,
    fieldPath,
    label,
    placeholder,
    tooltip,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { formData, updateFormData } = useCompanyForm();

    const selectedItems = useMemo(
        () => (Array.isArray(formData?.[fieldPath]) ? formData[fieldPath] : []),
        [fieldPath, formData],
    );

    const { data = [], isFetching } = useQuery({
        queryKey: ['wholesalerMultiSelectOptions', fieldPath, formData?.id],
        queryFn: async () => {
            const response = await axios.get(endpoint);
            return Array.isArray(response.data) ? response.data : [];
        },
        enabled: Boolean(formData?.id),
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const dropdownOptions = useMemo(() => data.map(toDropdownOption), [data]);

    const selectedIds = useMemo(
        () => selectedItems.map(getOptionId),
        [selectedItems],
    );

    const handleChange = (ids = []) => {
        const selectedRaw = (ids || [])
            .map(
                (id) => dropdownOptions.find((option) => option.id === id)?.raw,
            )
            .filter(Boolean);
        updateFormData(fieldPath, selectedRaw);
    };

    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                alignItems="flex-start"
                sx={{
                    ...stackStyling(),
                    height: 'auto',
                    paddingBottom: '15px',
                }}>
                <FormLabel sx={formLabelStyling(darkMode)}>{label}</FormLabel>
                <Box flex="1" maxWidth="400px" height="auto">
                    <DropDown
                        name={fieldPath}
                        isMulti
                        data={dropdownOptions}
                        shownValue="name"
                        multiValue={selectedIds}
                        alphabetical={false}
                        placeholder={placeholder}
                        darkMode={darkMode}
                        isLoading={isFetching}
                        handleSelectChange={handleChange}
                    />
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label={tooltip}
                        ariaLabel={`${fieldPath}-tip`}
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default WholesalerMultiSelectField;
